import React from 'react';

import { IMilestoneFields } from '../../../types/contentful';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';

import { getImageData } from '../../utils/contentful';

import RichText from '../RichText';
import cx from 'classnames';

import ImageLink from '../ImageLink';
import { normalizeHexCode } from '~/styles/utils';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import GlImage from '../Image';
import { Eyebrow } from '../Eyebrow';

interface Props extends IMilestoneFields {
  sysId?: string;
}

export function Milestone({
  headingText,
  eyebrow,
  image,
  imageLink,
  bodyCopy,
  disclaimer,
  backgroundColor,
  textColor,
  sysId,
  imageFirst,
  anchorLink,
  imageCornerRounding,
  imageDesktopFlushToEdges,
  imageMobileFlushToEdges
}: Props) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p className={`body-small text-${normalizeHexCode(textColor, true)}`}>
          {children}
        </p>
      );
    }
  };

  return (
    <div
      className="block-container"
      id={anchorLink ?? `${sysId}`}
      data-blockid={`milestone-${sysId}`}
      data-testid="milestone-block"
    >
      <div
        className={cx(
          'layout mx-5 flex-col items-center justify-center gap-0 self-stretch overflow-hidden rounded-3xl !p-0 tablet:mx-10 desktop:mx-15 desktop:!flex-nowrap desktop:rounded-4xl',
          {
            'tablet:flex-row-reverse': imageFirst,
            'tablet:flex-row': !imageFirst
          }
        )}
        style={{
          backgroundColor: normalizeHexCode(backgroundColor) ?? 'transparent'
        }}
      >
        <div
          className={cx(
            'column w-full flex-col items-center pb-4 tablet:w-1/2 tablet:py-4 desktop:py-8',
            {
              'tablet:pr-4 desktop:pl-10 desktop:pr-[72px]': imageFirst,
              'tablet:pl-4 desktop:pl-[72px] desktop:pr-10': !imageFirst
            }
          )}
        >
          <div className="flex w-full flex-col items-start px-4 pt-6 text-start tablet:p-4 desktop:p-12">
            {eyebrow && <Eyebrow eyebrowFields={eyebrow?.fields} />}
            <div className="mb-3 tablet:mb-6 desktop:max-w-[480px]">
              <h2
                className={`heading-2 text-${normalizeHexCode(
                  textColor,
                  true
                )}`}
              >
                {headingText}
              </h2>
            </div>
            {bodyCopy && (
              <div
                className={`w-full tablet:max-w-[480px] [&>a]:mt-6 [&>a]:w-full [&>a]:max-w-[258px] ${
                  disclaimer ? 'border-box mb-6' : 'border-box'
                }`}
              >
                <RichText
                  bulletColor={textColor}
                  data={bodyCopy}
                  overrideOptions={RICH_TEXT_OVERRIDE}
                />
              </div>
            )}
            {disclaimer && (
              <p
                className={`body-extra-small body-small max-w-[480px] whitespace-pre-wrap text-${normalizeHexCode(
                  textColor,
                  true
                )}`}
              >
                {disclaimer}
              </p>
            )}
          </div>
        </div>
        <div
          className={cx(
            'column w-full flex-col justify-center self-stretch px-4 py-4 tablet:w-1/2 tablet:py-4 desktop:py-8',
            imageMobileFlushToEdges
              ? '!p-0 tablet:px-4 tablet:pt-4'
              : 'px-4 pt-4',
            {
              'tablet:pl-4 desktop:pl-8': imageFirst,
              'tablet:pr-4 desktop:pr-8': !imageFirst,
              'desktop:!p-0': imageDesktopFlushToEdges,
              '[&_img]:overflow-hidden [&_img]:rounded-3xl [&_img]:desktop:rounded-4xl':
                imageCornerRounding
            }
          )}
        >
          {imageLink ? (
            <ImageLink
              {...{
                ...imageLink.fields
              }}
            >
              {(image0) => (
                <GlImage
                  className="block w-full max-w-full shrink-0"
                  {...getImageData(image0)}
                />
              )}
            </ImageLink>
          ) : (
            <GlImage
              className="block w-full max-w-full shrink-0"
              {...getImageData(image)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
